import React, { useState } from 'react';
import { Card, Button, Table, Select, DatePicker, Input, Radio, message } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useApolloClient } from '@apollo/client';
import type { ColumnsType } from 'antd/es/table';
import type { Dayjs } from 'dayjs';
import { GET_SETTLEMENT_DETAILS } from './graphql/queries';
import type { SettlementDetailType, GetSettlementDetailsResponse } from './graphql/types';

const CUSTOMERS: { label: string; value: string }[] = [
  { label: 'TEMU', value: 'TEMU' },
  { label: 'TEMU 半托', value: 'TEMU_SEMI' },
  { label: 'TIKTOK', value: 'TIKTOK' },
  { label: 'SHEIN', value: 'SHEIN' },
  { label: 'JD', value: 'JD' },
  { label: 'SF', value: 'SF' },
];
const PAGE_SIZE = 50;
// 运单号模式无游标翻页：一次查回全部命中，pageSize 仅作兜底上限（后端 MAX_PAGE_SIZE=1000）
const TRACKING_PAGE_SIZE = 1000;
const MAX_TRACKING_NOS = 500;

type QueryMode = 'date' | 'tracking';

const toYyyymmdd = (d: Dayjs): number => Number(d.format('YYYYMMDD'));

// 运单号文本按换行/逗号/空格分隔，trim 去空去重
const parseTrackingNumbers = (raw: string): string[] => {
  const list = raw
    .split(/[\s,]+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
  return Array.from(new Set(list));
};

const columns: ColumnsType<SettlementDetailType> = [
  { title: '明细ID', dataIndex: 'id', key: 'id', width: 100, fixed: 'left' },
  { title: '运单号', dataIndex: 'trackingNumber', key: 'trackingNumber', width: 160, fixed: 'left' },
  { title: '客户ID', dataIndex: 'customerId', key: 'customerId', width: 120 },
  { title: '司机ID', dataIndex: 'courierId', key: 'courierId', width: 120 },
  { title: '发票号', dataIndex: 'invoiceSerialNo', key: 'invoiceSerialNo', width: 160 },
  { title: '结算月份', dataIndex: 'settlementMonth', key: 'settlementMonth', width: 100 },
  { title: '结算日期', dataIndex: 'settlementDate', key: 'settlementDate', width: 110 },
  { title: '发件邮编', dataIndex: 'senderZipcode', key: 'senderZipcode', width: 100 },
  { title: '收件邮编', dataIndex: 'destZipcode', key: 'destZipcode', width: 100 },
  { title: '分区', dataIndex: 'zone', key: 'zone', width: 80 },
  { title: '长', dataIndex: 'dimensionLength', key: 'dimensionLength', width: 90 },
  { title: '宽', dataIndex: 'dimensionWidth', key: 'dimensionWidth', width: 90 },
  { title: '高', dataIndex: 'dimensionHeight', key: 'dimensionHeight', width: 90 },
  { title: '尺寸单位', dataIndex: 'uomOfDimension', key: 'uomOfDimension', width: 90 },
  { title: '实际重量', dataIndex: 'actualWeight', key: 'actualWeight', width: 100 },
  { title: '体积重量', dataIndex: 'dimensionWeight', key: 'dimensionWeight', width: 100 },
  { title: '计费重量', dataIndex: 'billedWeight', key: 'billedWeight', width: 100 },
  { title: '重量单位', dataIndex: 'uomOfWeight', key: 'uomOfWeight', width: 90 },
  { title: '币种', dataIndex: 'currency', key: 'currency', width: 80 },
  { title: '订单创建时间', dataIndex: 'orderCreateTime', key: 'orderCreateTime', width: 180 },
  { title: '揽收扫描时间', dataIndex: 'pickupScanTime', key: 'pickupScanTime', width: 180 },
  { title: '计费触发节点', dataIndex: 'billableEvent', key: 'billableEvent', width: 160 },
  { title: '计费触发时间', dataIndex: 'billableTriggerDate', key: 'billableTriggerDate', width: 180 },
  { title: '妥投事件', dataIndex: 'deliveredEvent', key: 'deliveredEvent', width: 160 },
  { title: '妥投时间', dataIndex: 'deliveredAt', key: 'deliveredAt', width: 180 },
  { title: '基础运费', dataIndex: 'freight', key: 'freight', width: 100 },
  { title: '附加费', dataIndex: 'surchargeList', key: 'surchargeList', width: 260 },
  { title: '费用', dataIndex: 'expenseSummarization', key: 'expenseSummarization', width: 140 },
];

const SettlementDetailTab: React.FC = () => {
  const client = useApolloClient();
  const [mode, setMode] = useState<QueryMode>('date');
  const [customer, setCustomer] = useState<string | undefined>(undefined);
  const [date, setDate] = useState<Dayjs | null>(null);
  const [trackingText, setTrackingText] = useState('');
  const [rows, setRows] = useState<SettlementDetailType[]>([]);
  const [loading, setLoading] = useState(false);
  // 游标翻页：每页首条前的 beginId 压栈，用于「上一页」回退；首页 beginId 不传
  const [cursorStack, setCursorStack] = useState<number[]>([]);

  // 返回查询变量；入参非法时提示并返回 null
  const buildVariables = (beginId?: number): Record<string, unknown> | null => {
    if (mode === 'tracking') {
      const trackingNumbers = parseTrackingNumbers(trackingText);
      if (trackingNumbers.length === 0) {
        message.error('请输入运单号');
        return null;
      }
      if (trackingNumbers.length > MAX_TRACKING_NOS) {
        message.error(`运单号数量不能超过 ${MAX_TRACKING_NOS} 条`);
        return null;
      }
      return { trackingNumbers, pageSize: TRACKING_PAGE_SIZE };
    }
    if (!customer) {
      message.error('请先选择客户');
      return null;
    }
    if (!date) {
      message.error('请选择日期');
      return null;
    }
    return {
      customer,
      date: toYyyymmdd(date),
      beginId: beginId ?? null,
      pageSize: PAGE_SIZE,
    };
  };

  const fetchPage = async (beginId?: number, stack?: number[]) => {
    const variables = buildVariables(beginId);
    if (!variables) {
      return;
    }
    setLoading(true);
    try {
      const res = await client.query<GetSettlementDetailsResponse>({
        query: GET_SETTLEMENT_DETAILS,
        variables,
        fetchPolicy: 'network-only',
      });
      setRows(res.data?.getSettlementDetails ?? []);
      if (stack) {
        setCursorStack(stack);
      }
    } catch (error: unknown) {
      message.error(`查询失败：${(error as Error)?.message || '未知错误'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => fetchPage(undefined, []);

  const handleNextPage = () => {
    if (rows.length === 0) {
      return;
    }
    const lastId = Number(rows[rows.length - 1].id);
    const firstId = Number(rows[0].id);
    fetchPage(lastId, [...cursorStack, firstId]);
  };

  const handlePrevPage = () => {
    if (cursorStack.length === 0) {
      return;
    }
    const prev = cursorStack[cursorStack.length - 1];
    const nextStack = cursorStack.slice(0, -1);
    // 上一页起点：栈顶记录的是上一页首条 id，回退需以「其前一条」为 beginId，即首页用 undefined
    fetchPage(nextStack.length === 0 ? undefined : prev - 1, nextStack);
  };

  const hasNext = rows.length === PAGE_SIZE;

  return (
    <Card title="明细查看">
      <div className="flex flex-col gap-5">
        <Radio.Group
          value={mode}
          onChange={(e) => {
            setMode(e.target.value);
            setRows([]);
            setCursorStack([]);
          }}
          optionType="button"
          buttonStyle="solid"
        >
          <Radio.Button value="date">客户 + 日期</Radio.Button>
          <Radio.Button value="tracking">运单号</Radio.Button>
        </Radio.Group>

        {mode === 'date' ? (
          <div className="flex flex-wrap items-center gap-3">
            <span className="whitespace-nowrap font-medium text-gray-700">
              <span className="mr-1 text-red-500">*</span>客户
            </span>
            <Select
              value={customer}
              onChange={setCustomer}
              placeholder="请选择客户"
              className="w-48"
            >
              {CUSTOMERS.map((c) => (
                <Select.Option key={c.value} value={c.value}>
                  {c.label}
                </Select.Option>
              ))}
            </Select>
            <span className="whitespace-nowrap font-medium text-gray-700">
              <span className="mr-1 text-red-500">*</span>日期
            </span>
            <DatePicker value={date} onChange={(v) => setDate(v)} />
            <Button
              type="primary"
              icon={<SearchOutlined />}
              loading={loading}
              onClick={handleSearch}
            >
              查询
            </Button>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <span className="whitespace-nowrap font-medium text-gray-700">
              <span className="mr-1 text-red-500">*</span>运单号（换行、逗号或空格分隔，最多 {MAX_TRACKING_NOS} 条）
            </span>
            <Input.TextArea
              value={trackingText}
              onChange={(e) => setTrackingText(e.target.value)}
              placeholder="每行一个运单号，或用逗号/空格分隔"
              rows={4}
              className="max-w-2xl"
            />
            <div>
              <Button
                type="primary"
                icon={<SearchOutlined />}
                loading={loading}
                onClick={handleSearch}
              >
                查询
              </Button>
            </div>
          </div>
        )}

        <div className="text-gray-500">共 {rows.length} 条</div>

        <Table
          rowKey="id"
          columns={columns}
          dataSource={rows}
          loading={loading}
          pagination={false}
          scroll={{ x: 3760 }}
          locale={{ emptyText: '暂无数据' }}
        />
        {mode === 'date' && (
          <div className="flex justify-end gap-2">
            <Button disabled={loading || cursorStack.length === 0} onClick={handlePrevPage}>
              上一页
            </Button>
            <Button disabled={loading || !hasNext} onClick={handleNextPage}>
              下一页
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
};

export default SettlementDetailTab;
